import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  ExpandMore,
  ChevronRight,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DriveFileMove as MoveIcon,
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { FileSystemNode, FileSystemMetadata } from '../types/api';
import FolderSelectorDialog from './FolderSelectorDialog';
import InputModal from './InputModal';
import ContextMenu, { ContextMenuPosition } from './ContextMenu';

interface FileSystemTreeProps {
  fileSystem: FileSystemMetadata | null;
  loading: boolean;
  onFileClick: (file: FileSystemNode) => void;
  onCreateFile: (parentId: string, name: string) => Promise<void>;
  onCreateFolder: (parentId: string, name: string) => Promise<void>;
  onUploadFile: (parentId: string, file: File) => Promise<void>;
  onDeleteNode: (nodeId: string) => Promise<void>;
  onRenameNode: (nodeId: string, newName: string) => Promise<void>;
  onMoveNode: (nodeId: string, newParentId: string, newOrder: number) => Promise<void>;
  // Optional array of folder IDs that should be initially expanded (for state restoration)
  initialExpandedFolders?: string[];
  // Optional callback when expanded folders change (for state tracking)
  onExpandedFoldersChange?: (expandedFolderIds: string[]) => void;
}

interface TreeNodeProps {
  node: FileSystemNode;
  nodes: Record<string, FileSystemNode>;
  level: number;
  onFileClick: (file: FileSystemNode) => void;
  onCreateFile: (parentId: string, name: string) => Promise<void>;
  onCreateFolder: (parentId: string, name: string) => Promise<void>;
  onUploadFile: (parentId: string, file: File) => Promise<void>;
  onDeleteNode: (nodeId: string) => Promise<void>;
  onRenameNode: (nodeId: string, newName: string) => Promise<void>;
  onMoveNode: (nodeId: string, newParentId: string, newOrder: number) => Promise<void>;
  fileSystem: FileSystemMetadata;
  expandedFolders: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  onOpenInputModal: (type: 'file' | 'folder', parentId: string) => void;
  onOpenFileUpload: (parentId: string) => void;
}

interface SearchNodeEntry {
  node: FileSystemNode;
  path: string;
}

const normalizeForSearch = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const LONG_PRESS_DURATION_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  nodes,
  level,
  onFileClick,
  onCreateFile,
  onCreateFolder,
  onUploadFile,
  onDeleteNode,
  onRenameNode,
  onMoveNode,
  fileSystem,
  expandedFolders,
  onToggleExpand,
  onOpenInputModal,
  onOpenFileUpload,
}) => {
  const isExpanded = expandedFolders.has(node.id);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameName, setRenameName] = useState(node.name);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

  const isFolder = node.type === 'folder';
  const hasChildren = isFolder && node.children && node.children.length > 0;

  const handleToggle = () => {
    if (isFolder) {
      onToggleExpand(node.id);
    } else {
      onFileClick(node);
    }
  };

  const handleOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setAnchorEl(null);
  };

  const handleCreateClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (isFolder) {
      setCreateMenuAnchorEl(event.currentTarget);
    }
  };

  const handleCreateMenuClose = () => {
    setCreateMenuAnchorEl(null);
  };

  const handleCreateFile = () => {
    // Expand folder BEFORE creating file to avoid jump
    if (!isExpanded) {
      onToggleExpand(node.id);
    }
    onOpenInputModal('file', node.id);
    handleCreateMenuClose();
  };

  const handleCreateFolder = () => {
    // Expand folder BEFORE creating subfolder to avoid jump
    if (!isExpanded) {
      onToggleExpand(node.id);
    }
    onOpenInputModal('folder', node.id);
    handleCreateMenuClose();
  };

  const handleUploadFile = () => {
    // Expand folder BEFORE uploading file to avoid jump
    if (!isExpanded) {
      onToggleExpand(node.id);
    }
    onOpenFileUpload(node.id);
    handleCreateMenuClose();
  };

  const handleRename = () => {
    handleOptionsClose();
    // Delay setting isRenaming to allow menu to close and avoid immediate blur
    setTimeout(() => {
      setIsRenaming(true);
    }, 100);
  };

  const handleRenameSubmit = async () => {
    if (renameName.trim() && renameName.trim() !== node.name) {
      await onRenameNode(node.id, renameName.trim());
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setRenameName(node.name);
      setIsRenaming(false);
    }
  };

  const handleDelete = async () => {
    const confirmMessage = isFolder
      ? `Are you sure you want to delete the folder "${node.name}" and all its contents?`
      : `Are you sure you want to delete the file "${node.name}"?`;
    
    if (window.confirm(confirmMessage)) {
      await onDeleteNode(node.id);
    }
    handleOptionsClose();
  };

  const handleMove = () => {
    setIsMoveDialogOpen(true);
    handleOptionsClose();
  };

  const handleMoveConfirm = async (newParentId: string) => {
    await onMoveNode(node.id, newParentId, 0);
    setIsMoveDialogOpen(false);
  };

  const handleMoveCancel = () => {
    setIsMoveDialogOpen(false);
  };

  const children = hasChildren
    ? node.children!.map(childId => nodes[childId]).filter(Boolean)
    : [];

  // Sort children: folders first, then files, both alphabetically
  children.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === 'folder' ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: level * 2,
        }}
        secondaryAction={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isFolder && (
              <IconButton
                edge="end"
                size="small"
                onClick={handleCreateClick}
                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              edge="end"
              size="small"
              onClick={handleOptionsClick}
              sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <ListItemButton onClick={handleToggle} sx={{ pr: 10 }}>
          {isFolder && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              {isExpanded ? <ExpandMore /> : <ChevronRight />}
            </ListItemIcon>
          )}
          <ListItemIcon sx={{ minWidth: 36 }}>
            {isFolder ? (
              isExpanded ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />
            ) : (
              <FileIcon color="action" />
            )}
          </ListItemIcon>
          {isRenaming ? (
            <input
              type="text"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onBlur={handleRenameSubmit}
              onKeyDown={handleRenameKeyDown}
              autoFocus
              style={{
                border: '1px solid #666',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '0.875rem',
                width: '100%',
                backgroundColor: '#2A2A2A',
                color: '#ffffff',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.border = '2px solid #32CA1D'}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <ListItemText
              primary={node.name}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { fontWeight: isFolder ? 500 : 400 },
              }}
            />
          )}
        </ListItemButton>
      </ListItem>

      {/* Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleOptionsClose}
      >
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMove}>
          <ListItemIcon>
            <MoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move to...</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Move Dialog */}
      <FolderSelectorDialog
        open={isMoveDialogOpen}
        onClose={handleMoveCancel}
        onSelect={handleMoveConfirm}
        fileSystem={fileSystem}
        currentNodeId={node.id}
        currentParentId={node.parentId}
      />

      {/* Create Menu (for folders) */}
      <Menu
        anchorEl={createMenuAnchorEl}
        open={Boolean(createMenuAnchorEl)}
        onClose={handleCreateMenuClose}
      >
        <MenuItem onClick={handleCreateFile}>
          <ListItemIcon>
            <FileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New File</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCreateFolder}>
          <ListItemIcon>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New Folder</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUploadFile}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload File</ListItemText>
        </MenuItem>
      </Menu>

      {/* Children */}
      {isFolder && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                nodes={nodes}
                level={level + 1}
                onFileClick={onFileClick}
                onCreateFile={onCreateFile}
                onCreateFolder={onCreateFolder}
                onUploadFile={onUploadFile}
                onDeleteNode={onDeleteNode}
                onRenameNode={onRenameNode}
                onMoveNode={onMoveNode}
                fileSystem={fileSystem}
                expandedFolders={expandedFolders}
                onToggleExpand={onToggleExpand}
                onOpenInputModal={onOpenInputModal}
                onOpenFileUpload={onOpenFileUpload}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const FileSystemTree: React.FC<FileSystemTreeProps> = ({
  fileSystem,
  loading,
  onFileClick,
  onCreateFile,
  onCreateFolder,
  onUploadFile,
  onDeleteNode,
  onRenameNode,
  onMoveNode,
  initialExpandedFolders = [],
  onExpandedFoldersChange,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const searchContainerRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadParentId, setUploadParentId] = useState<string>('');
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState<null | HTMLElement>(null);
  // Initialize expandedFolders with the initial expanded folders (if any)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(initialExpandedFolders)
  );
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [inputModalType, setInputModalType] = useState<'file' | 'folder'>('file');
  const [inputModalParentId, setInputModalParentId] = useState<string>('');
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const touchStartPointRef = useRef<{ x: number; y: number } | null>(null);
  const suppressNextClickRef = useRef(false);

  // Effect to restore expanded folders when prop changes (e.g., when navigating back from editor)
  // This ensures folders are expanded even if the component is already mounted
  useEffect(() => {
    if (initialExpandedFolders.length > 0) {
      setExpandedFolders(new Set(initialExpandedFolders));
    }
  }, [initialExpandedFolders]);

  useEffect(() => {
    if (isSearchVisible) {
      searchInputRef.current?.focus();
    }
  }, [isSearchVisible]);

  useEffect(() => {
    return () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
  }, []);

  const nodes = useMemo(() => fileSystem?.nodes ?? {}, [fileSystem]);
  const rootId = useMemo(() => fileSystem?.rootId ?? '', [fileSystem]);
  const rootNode = useMemo(() => (rootId ? nodes[rootId] : undefined), [nodes, rootId]);

  const handleCreateClick = (event: React.MouseEvent<HTMLElement>) => {
    setCreateMenuAnchorEl(event.currentTarget);
  };

  const handleCreateMenuClose = () => {
    setCreateMenuAnchorEl(null);
  };

  const commitExpandedFolders = (next: Set<string>) => {
    setExpandedFolders(next);

    if (onExpandedFoldersChange) {
      setTimeout(() => {
        onExpandedFoldersChange(Array.from(next));
      }, 0);
    }
  };

  const handleToggleExpand = (nodeId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }

      if (onExpandedFoldersChange) {
        setTimeout(() => {
          onExpandedFoldersChange(Array.from(next));
        }, 0);
      }
      
      return next;
    });
  };

  const handleTreeContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuPosition({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
  };

  const clearLongPressTimeout = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  const handleTreeTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) {
      clearLongPressTimeout();
      touchStartPointRef.current = null;
      longPressTriggeredRef.current = false;
      return;
    }

    const touch = event.touches[0];
    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    longPressTriggeredRef.current = false;
    clearLongPressTimeout();

    longPressTimeoutRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      suppressNextClickRef.current = true;
      setContextMenuPosition({ mouseX: touch.clientX - 2, mouseY: touch.clientY - 4 });
      longPressTimeoutRef.current = null;
    }, LONG_PRESS_DURATION_MS);
  };

  const handleTreeTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartPointRef.current || event.touches.length !== 1) {
      clearLongPressTimeout();
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPointRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPointRef.current.y);

    if (deltaX > LONG_PRESS_MOVE_TOLERANCE_PX || deltaY > LONG_PRESS_MOVE_TOLERANCE_PX) {
      clearLongPressTimeout();
    }
  };

  const handleTreeTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    clearLongPressTimeout();

    if (longPressTriggeredRef.current) {
      event.preventDefault();
      event.stopPropagation();
      longPressTriggeredRef.current = false;
    }

    touchStartPointRef.current = null;
  };

  const handleTreeTouchCancel = () => {
    clearLongPressTimeout();
    longPressTriggeredRef.current = false;
    touchStartPointRef.current = null;
  };

  const handleTreeClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressNextClickRef.current) {
      return;
    }

    suppressNextClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleCloseContextMenu = () => {
    setContextMenuPosition(null);
  };

  const handleOpenSearch = () => {
    setIsSearchVisible(true);
    handleCloseContextMenu();
  };

  const handleSearchClickAway = (event: MouseEvent | TouchEvent) => {
    if (!searchContainerRef.current?.contains(event.target as Node) && !searchQuery.trim()) {
      setIsSearchVisible(false);
    }
  };

  const expandFolderPath = (folderId: string) => {
    const next = new Set(expandedFolders);
    let currentId: string | undefined = folderId;

    while (currentId && currentId !== rootId) {
      const currentNode: FileSystemNode | undefined = nodes[currentId];
      if (!currentNode) {
        break;
      }

      if (currentNode.type === 'folder') {
        next.add(currentId);
      }

      currentId = currentNode.parentId || undefined;
    }

    commitExpandedFolders(next);
  };

  const handleCreateFile = () => {
    setInputModalType('file');
    setInputModalParentId(rootId);
    setInputModalOpen(true);
    handleCreateMenuClose();
  };

  const handleCreateFolder = () => {
    setInputModalType('folder');
    setInputModalParentId(rootId);
    setInputModalOpen(true);
    handleCreateMenuClose();
  };

  const handleUploadFileClick = () => {
    setUploadParentId(rootId);
    handleCreateMenuClose();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && uploadParentId) {
      await onUploadFile(uploadParentId, file);
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenFileUpload = (parentId: string) => {
    setUploadParentId(parentId);
    fileInputRef.current?.click();
  };

  const handleOpenInputModal = (type: 'file' | 'folder', parentId: string) => {
    setInputModalType(type);
    setInputModalParentId(parentId);
    setInputModalOpen(true);
  };

  const handleInputModalConfirm = async (value: string) => {
    if (inputModalType === 'file') {
      const name = value.endsWith('.md') ? value : `${value}.md`;
      await onCreateFile(inputModalParentId, name);
    } else {
      await onCreateFolder(inputModalParentId, value);
    }
    setInputModalOpen(false);
  };

  const handleInputModalCancel = () => {
    setInputModalOpen(false);
  };

  const children = rootNode?.children
    ? rootNode.children.map(childId => nodes[childId]).filter(Boolean)
    : [];

  const searchableNodes = useMemo<SearchNodeEntry[]>(() => {
    const results: SearchNodeEntry[] = [];

    if (!rootNode) {
      return results;
    }

    const walk = (nodeId: string, parentPath: string) => {
      const node = nodes[nodeId];
      if (!node) return;

      const currentPath = parentPath ? `${parentPath} / ${node.name}` : node.name;
      results.push({ node, path: currentPath });

      if (node.type === 'folder' && node.children) {
        node.children.forEach((childId) => walk(childId, currentPath));
      }
    };

    rootNode.children?.forEach((childId) => walk(childId, ''));
    return results;
  }, [nodes, rootNode]);

  const normalizedQuery = normalizeForSearch(searchQuery);
  const hasActiveSearch = normalizedQuery.length > 0;
  const showSearchResults = isSearchVisible && hasActiveSearch;

  const searchResults = useMemo<SearchNodeEntry[]>(() => {
    if (!hasActiveSearch) {
      return [];
    }

    const scoredMatches = searchableNodes
      .map((entry) => {
        const normalizedName = normalizeForSearch(entry.node.name);
        const index = normalizedName.indexOf(normalizedQuery);

        if (index === -1) {
          return null;
        }

        let score = 2;
        if (normalizedName === normalizedQuery) {
          score = 0;
        } else if (normalizedName.startsWith(normalizedQuery)) {
          score = 1;
        }

        return { entry, index, score };
      })
      .filter((match): match is { entry: SearchNodeEntry; index: number; score: number } => Boolean(match));

    scoredMatches.sort((a, b) => {
      if (a.score !== b.score) {
        return a.score - b.score;
      }

      if (a.index !== b.index) {
        return a.index - b.index;
      }

      if (a.entry.node.type !== b.entry.node.type) {
        return a.entry.node.type === 'folder' ? -1 : 1;
      }

      return a.entry.node.name.localeCompare(b.entry.node.name);
    });

    return scoredMatches.map((match) => match.entry);
  }, [hasActiveSearch, normalizedQuery, searchableNodes]);

  const handleSearchResultClick = (result: SearchNodeEntry) => {
    if (result.node.type === 'file') {
      onFileClick(result.node);
      return;
    }

    expandFolderPath(result.node.id);
    setSearchQuery('');
    setIsSearchVisible(false);
  };

  // Sort children: folders first, then files, both alphabetically
  children.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === 'folder' ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!fileSystem || !fileSystem.nodes) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No files yet
        </Typography>
      </Box>
    );
  }

  if (!rootNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Error loading file system
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        '& input, & textarea, & [contenteditable="true"]': {
          userSelect: 'text',
          WebkitUserSelect: 'text',
          WebkitTouchCallout: 'default',
        },
      }}
      onContextMenu={handleTreeContextMenu}
      onTouchStart={handleTreeTouchStart}
      onTouchMove={handleTreeTouchMove}
      onTouchEnd={handleTreeTouchEnd}
      onTouchCancel={handleTreeTouchCancel}
      onClickCapture={handleTreeClickCapture}
      data-testid="file-system-tree"
    >
      {isSearchVisible && (
        <ClickAwayListener onClickAway={handleSearchClickAway}>
          <Box ref={searchContainerRef} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search all file and folder names"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery('')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </ClickAwayListener>
      )}

      {/* Root level add button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {showSearchResults ? `Search Results (${searchResults.length})` : 'Files & Folders'}
        </Typography>
        <IconButton
          size="small"
          onClick={handleCreateClick}
          sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Create Menu */}
      <Menu
        anchorEl={createMenuAnchorEl}
        open={Boolean(createMenuAnchorEl)}
        onClose={handleCreateMenuClose}
      >
        <MenuItem onClick={handleCreateFile}>
          <ListItemIcon>
            <FileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New File</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCreateFolder}>
          <ListItemIcon>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New Folder</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUploadFileClick}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload File</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.md,.txt"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <ContextMenu position={contextMenuPosition} onClose={handleCloseContextMenu}>
        <MenuItem onClick={handleOpenSearch}>
          <ListItemIcon>
            <SearchIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Search</ListItemText>
        </MenuItem>
      </ContextMenu>

      {/* File tree */}
      <List sx={{ width: '100%', py: 0 }}>
        {showSearchResults ? (
          searchResults.length === 0 ? (
            <ListItem>
              <ListItemText
                primary='No matches found'
                secondary='Try a different file or folder name'
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary',
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                }}
              />
            </ListItem>
          ) : (
            searchResults.map((result) => {
              const isFolder = result.node.type === 'folder';
              return (
                <ListItem key={`search-${result.node.id}`} disablePadding>
                  <ListItemButton onClick={() => handleSearchResultClick(result)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {isFolder ? <FolderIcon color="primary" /> : <FileIcon color="action" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.node.name}
                      secondary={result.path}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontWeight: isFolder ? 500 : 400 },
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )
        ) : children.length === 0 ? (
          <ListItem>
            <ListItemText
              primary='Click the "plus" icon to add content'
              primaryTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            />
          </ListItem>
        ) : (
          children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              nodes={nodes}
              level={0}
              onFileClick={onFileClick}
              onCreateFile={onCreateFile}
              onCreateFolder={onCreateFolder}
              onUploadFile={onUploadFile}
              onDeleteNode={onDeleteNode}
              onRenameNode={onRenameNode}
              onMoveNode={onMoveNode}
              fileSystem={fileSystem}
              expandedFolders={expandedFolders}
              onToggleExpand={handleToggleExpand}
              onOpenInputModal={handleOpenInputModal}
              onOpenFileUpload={handleOpenFileUpload}
            />
          ))
        )}
      </List>

      {/* Input Modal for creating files/folders */}
      <InputModal
        open={inputModalOpen}
        title={inputModalType === 'file' ? 'Create New File' : 'Create New Folder'}
        label={inputModalType === 'file' ? 'File Name' : 'Folder Name'}
        placeholder={inputModalType === 'file' ? 'File Name' : 'Folder Name'}
        onConfirm={handleInputModalConfirm}
        onCancel={handleInputModalCancel}
      />
    </Box>
  );
};

export default FileSystemTree;
